/**
 * Extracts a human-readable string from any thrown error.
 *
 * Priority order for Axios response errors:
 *   detail → message → Error → messages[] → fallback
 * For Axios network/timeout errors (no response): fallback
 * For plain JS errors (throw new Error / throw "string"): error.message / string
 *
 * Handles non-string values (objects / arrays) safely via JSON.stringify
 * so React never receives a non-renderable value.
 *
 * @param {unknown} error    The caught error.
 * @param {string}  fallback Returned when no message can be extracted.
 * @returns {string}
 */
const getFirstArrayMessage = value => {
  const first = value?.[0];

  if (first?.message) return first.message;
  if (typeof first === 'string') return first;

  return null;
};

const getSerializableMessage = (value, fallback) => {
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
};

const getMessageFromResponseData = (data, fallback) => {
  const candidates = [data.detail, data.message, data.Error, data.messages];

  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    if (typeof candidate === 'string') return candidate;

    if (Array.isArray(candidate)) {
      const arrayMessage = getFirstArrayMessage(candidate);
      if (arrayMessage) return arrayMessage;
    }

    return getSerializableMessage(candidate, fallback);
  }

  return fallback;
};

const getPlainErrorMessage = error => {
  if (typeof error === 'string') return error;
  if (typeof error?.message === 'string' && error.message) return error.message;

  return null;
};

const getErrorMessage = (
  error,
  fallback = 'Something went wrong. Please try again.'
) => {
  if (!error) return fallback;

  const data = error?.response?.data;
  if (data != null) {
    // Try field names in priority order: detail → message → Error → messages[]
    return getMessageFromResponseData(data, fallback);
  }

  // Axios error with no response (network error, timeout, CORS, etc.)
  // error.message is Axios-internal ("Network Error") — not user-friendly
  if (error.isAxiosError || error.config) return fallback;

  // Plain JS Error — not an Axios error, message is developer-controlled
  const plainMessage = getPlainErrorMessage(error);
  if (plainMessage) return plainMessage;

  return fallback;
};

export { getErrorMessage };
